import React from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import {
  Container,
  Box,
  Button,
  Image,
  Flex,
  Link,
  Text
} from 'pcln-design-system'
import { usePartner } from '@pcln/whitelabel-components'
import analytics from '@/shared-utils/analytics'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '@/constants/analytics'
import { GenericBannerContent } from '@/types'

const FlexDesktopOnly = styled(Flex)`
  display: none;
  ${themeGet('mediaQueries.lg')} {
    display: flex;
  }
`

const StyledImage = styled(Image)`
  max-height: 350px;
`

const gaCategories: Record<string, string> = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
}

const TextSpan = Text.span

type GenericBannerProps = {
  content: GenericBannerContent
  children?: string | React.ReactNode
  headingFontColor?: string
  smallHeadingFontColor?: string
  backgroundColor?: string
  imagePosition?: string
}

const GenericBanner = (props: GenericBannerProps) => {
  const router = useRouter()
  const pathname = router?.pathname
  const gaCategory = gaCategories[pathname] || ''
  const {
    content,
    headingFontColor = 'text.base',
    smallHeadingFontColor = 'text.light',
    backgroundColor = 'background.light'
  } = props
  const {
    buttons,
    contentText,
    gaAction,
    headingText,
    smallHeadingText,
    image,
    alt
  } = content

  const partner = usePartner()
  const adBannerButtons =
    buttons &&
    buttons.map(button => {
      return (
        <Link
          key={`${button.gaLabel} Button`}
          href={button.linkUrl}
          target="_blank"
          rel="noopener"
          aria-label={`${button.gaLabel} Promo link`}
          onClick={() => {
            if (typeof partner === 'undefined') {
              analytics.fireEvent({
                action: gaAction,
                category: gaCategory,
                label: button.gaLabel
              })
            }
          }}
        >
          <Button data-testid={`${button.gaLabel}-button`}>
            {button.buttonText}
          </Button>
        </Link>
      )
    })
  return (
    <Container>
      <Flex
        flexDirection="row"
        alignItems="center"
        borderRadius="xl"
        boxShadowSize="sm"
        m={4}
        p={4}
        color={backgroundColor}
      >
        <Flex
          flexDirection="column"
          px={1}
          textAlign={['center', null, null, 'left']}
          width={1}
        >
          <Box mb={3}>
            <Text
              caps
              color={smallHeadingFontColor}
              fontSize={0}
              fontWeight="bold"
              textStyle="overline"
              mb={3}
            >
              {smallHeadingText}
            </Text>
            <Text color={headingFontColor} textStyle="heading2" mb={3}>
              {headingText}
            </Text>
            <TextSpan textStyle="article" color="text.dark" mb={2}>
              {contentText}
            </TextSpan>
          </Box>
          {adBannerButtons}
        </Flex>
        <FlexDesktopOnly px={1} width={1} ml={6} justifyContent="center">
          <StyledImage
            loading="lazy"
            alt={alt}
            src={image}
            borderRadius="xl"
            height="100%"
          />
        </FlexDesktopOnly>
      </Flex>
    </Container>
  )
}

export default GenericBanner
